import { buildICal, buildMailto, buildVCard } from "../utils/qrFormats";

export const QR_TOOLS = [
    {
        id: "qr-text",
        label: "Text / URL",
        path: "/qr/text",
        description: "Encode plain text or a URL exactly as entered.",
        fields: [
            {
                key: "text",
                label: "Text or URL",
                type: "textarea",
                placeholder: "https://example.com",
                defaultValue: "",
            },
        ],
        build: (v) => v.text || "",
    },
    {
        id: "qr-wifi",
        label: "Wi-Fi",
        path: "/qr/wifi",
        description: "Generate a Wi-Fi QR with SSID, security, and password.",
        fields: [
            {
                key: "ssid",
                label: "SSID",
                type: "text",
                placeholder: "Network name",
                defaultValue: "",
            },
            {
                key: "password",
                label: "Password",
                type: "text",
                placeholder: "Network password",
                defaultValue: "",
            },
            {
                key: "security",
                label: "Security",
                type: "select",
                options: [
                    { label: "WPA/WPA2", value: "WPA" },
                    { label: "WEP", value: "WEP" },
                    { label: "None", value: "nopass" },
                ],
                defaultValue: "WPA",
            },
            {
                key: "hidden",
                label: "Hidden Network",
                type: "checkbox",
                defaultValue: false,
            },
        ],
        build: (v) => {
            const security = v.security || "WPA";
            const hidden = v.hidden ? "true" : "false";
            const password = security === "nopass" ? "" : v.password || "";
            return `WIFI:T:${security};S:${v.ssid || ""};P:${password};H:${hidden};;`;
        },
    },
    {
        id: "qr-contact",
        label: "Contact (vCard)",
        path: "/qr/contact",
        description: "Create a vCard with name, phone, email, and address.",
        fields: [
            {
                key: "firstName",
                label: "First Name",
                type: "text",
                placeholder: "Jane",
                defaultValue: "",
            },
            {
                key: "lastName",
                label: "Last Name",
                type: "text",
                placeholder: "Doe",
                defaultValue: "",
            },
            {
                key: "org",
                label: "Organization",
                type: "text",
                placeholder: "Company",
                defaultValue: "",
            },
            {
                key: "title",
                label: "Title",
                type: "text",
                placeholder: "Role",
                defaultValue: "",
            },
            {
                key: "phone",
                label: "Phone",
                type: "text",
                placeholder: "+1 555 555 5555",
                defaultValue: "",
            },
            {
                key: "email",
                label: "Email",
                type: "text",
                placeholder: "jane@example.com",
                defaultValue: "",
            },
            {
                key: "website",
                label: "Website",
                type: "text",
                placeholder: "https://example.com",
                defaultValue: "",
            },
            {
                key: "street",
                label: "Street",
                type: "text",
                placeholder: "123 Main St",
                defaultValue: "",
            },
            {
                key: "city",
                label: "City",
                type: "text",
                placeholder: "City",
                defaultValue: "",
            },
            {
                key: "state",
                label: "State",
                type: "text",
                placeholder: "State",
                defaultValue: "",
            },
            {
                key: "zip",
                label: "ZIP",
                type: "text",
                placeholder: "ZIP",
                defaultValue: "",
            },
            {
                key: "country",
                label: "Country",
                type: "text",
                placeholder: "Country",
                defaultValue: "",
            },
        ],
        build: (v) => buildVCard(v),
    },
    {
        id: "qr-email",
        label: "Email",
        path: "/qr/email",
        description: "Build a mailto link with recipient, subject, and body.",
        fields: [
            {
                key: "to",
                label: "To",
                type: "text",
                placeholder: "name@example.com",
                defaultValue: "",
            },
            {
                key: "subject",
                label: "Subject",
                type: "text",
                placeholder: "Hello",
                defaultValue: "",
            },
            {
                key: "body",
                label: "Body",
                type: "textarea",
                placeholder: "Message",
                defaultValue: "",
            },
        ],
        build: (v) => buildMailto(v),
    },
    {
        id: "qr-sms",
        label: "SMS",
        path: "/qr/sms",
        description: "Create an SMS prompt with number and message.",
        fields: [
            {
                key: "number",
                label: "Phone Number",
                type: "text",
                placeholder: "+15555555555",
                defaultValue: "",
            },
            {
                key: "message",
                label: "Message",
                type: "textarea",
                placeholder: "SMS message",
                defaultValue: "",
            },
        ],
        build: (v) => `SMSTO:${v.number || ""}:${v.message || ""}`,
    },
    {
        id: "qr-phone",
        label: "Phone",
        path: "/qr/phone",
        description: "Create a tap-to-call phone link.",
        fields: [
            {
                key: "number",
                label: "Phone Number",
                type: "text",
                placeholder: "+15555555555",
                defaultValue: "",
            },
        ],
        build: (v) => `tel:${v.number || ""}`,
    },
    {
        id: "qr-location",
        label: "Location",
        path: "/qr/location",
        description: "Share a geo location with latitude, longitude, and label.",
        fields: [
            {
                key: "lat",
                label: "Latitude",
                type: "text",
                placeholder: "37.7749",
                defaultValue: "",
            },
            {
                key: "lon",
                label: "Longitude",
                type: "text",
                placeholder: "-122.4194",
                defaultValue: "",
            },
            {
                key: "label",
                label: "Label",
                type: "text",
                placeholder: "San Francisco",
                defaultValue: "",
            },
        ],
        build: (v) => {
            const label = v.label ? `?q=${encodeURIComponent(v.label)}` : "";
            return `geo:${v.lat || ""},${v.lon || ""}${label}`;
        },
    },
    {
        id: "qr-event",
        label: "Event",
        path: "/qr/event",
        description: "Generate an iCal event with title, time, and location.",
        fields: [
            {
                key: "title",
                label: "Title",
                type: "text",
                placeholder: "Event title",
                defaultValue: "",
            },
            {
                key: "start",
                label: "Start",
                type: "datetime-local",
                defaultValue: "",
            },
            {
                key: "end",
                label: "End",
                type: "datetime-local",
                defaultValue: "",
            },
            {
                key: "location",
                label: "Location",
                type: "text",
                placeholder: "Venue",
                defaultValue: "",
            },
            {
                key: "description",
                label: "Description",
                type: "textarea",
                placeholder: "Details",
                defaultValue: "",
            },
        ],
        build: (v) => buildICal(v),
    },
];

export const BARCODE_TOOLS = [
    {
        id: "barcode-code128",
        label: "Code 128",
        path: "/barcode/code-128",
        bcid: "code128",
        description: "Flexible alphanumeric barcode for general labels.",
        guidance: "Alphanumeric + limited symbols.",
        defaultValue: "CODE128",
    },
    {
        id: "barcode-code39",
        label: "Code 39",
        path: "/barcode/code-39",
        bcid: "code39",
        description: "Classic alphanumeric barcode for inventory and assets.",
        guidance: "Alphanumeric + limited symbols.",
        defaultValue: "CODE39",
    },
    {
        id: "barcode-code93",
        label: "Code 93",
        path: "/barcode/code-93",
        bcid: "code93",
        description: "Compact alphanumeric barcode with higher density.",
        guidance: "Alphanumeric + limited symbols.",
        defaultValue: "CODE93",
    },
    {
        id: "barcode-ean13",
        label: "EAN-13",
        path: "/barcode/ean-13",
        bcid: "ean13",
        description: "Retail product barcode (12 digits, checksum auto).",
        guidance: "12 digits.",
        defaultValue: "590123412345",
    },
    {
        id: "barcode-ean8",
        label: "EAN-8",
        path: "/barcode/ean-8",
        bcid: "ean8",
        description: "Short retail barcode (7 digits, checksum auto).",
        guidance: "7 digits.",
        defaultValue: "1234567",
    },
    {
        id: "barcode-upca",
        label: "UPC-A",
        path: "/barcode/upc-a",
        bcid: "upca",
        description: "US retail barcode (11 digits, checksum auto).",
        guidance: "11 digits.",
        defaultValue: "12345678901",
    },
    {
        id: "barcode-upce",
        label: "UPC-E",
        path: "/barcode/upc-e",
        bcid: "upce",
        description: "Compressed UPC for small packages (7–8 digits).",
        guidance: "7 or 8 digits.",
        defaultValue: "1234567",
    },
    {
        id: "barcode-itf",
        label: "ITF (Interleaved 2 of 5)",
        path: "/barcode/itf",
        bcid: "interleaved2of5",
        description: "Interleaved 2 of 5 for numeric pairs.",
        guidance: "Even number of digits.",
        defaultValue: "123456",
    },
    {
        id: "barcode-itf14",
        label: "ITF-14",
        path: "/barcode/itf-14",
        bcid: "itf14",
        description: "Shipping/container barcode (13–14 digits).",
        guidance: "13 or 14 digits.",
        defaultValue: "1234567890123",
    },
    {
        id: "barcode-codabar",
        label: "Codabar",
        path: "/barcode/codabar",
        bcid: "rationalizedCodabar",
        description: "Library/blood bank barcode with A–D guards.",
        guidance: "Digits with A–D guards.",
        defaultValue: "A123456A",
    },
    {
        id: "barcode-msi",
        label: "MSI",
        path: "/barcode/msi",
        bcid: "msi",
        description: "Numeric barcode used in inventory systems.",
        guidance: "Numeric only.",
        defaultValue: "1234567",
    },
    {
        id: "barcode-pharmacode",
        label: "Pharmacode",
        path: "/barcode/pharmacode",
        bcid: "pharmacode",
        description: "Pharmaceutical packaging code (3–131070).",
        guidance: "Number between 3–131070.",
        defaultValue: "1234",
    },
];

export const TOOL_GROUPS = [
    { title: "QR Generator", items: QR_TOOLS },
    { title: "Barcode Generator", items: BARCODE_TOOLS },
];
